import type { LucideIcon } from 'lucide-react'
import { Circle, Eye, X, ChevronRight } from 'lucide-react'

export type AccessMode = 'none' | 'read' | 'write' | 'read_write'

export interface NavItem {
  label: string
  href: string
  icon?: LucideIcon
  isActive?: boolean
  badge?: string | number
  accessMode?: AccessMode
}

export interface ContextualNav {
  entityName: string
  entityIcon: LucideIcon
  entityId: string
  entityType: 'association' | 'circle'
  items: NavItem[]
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

export interface MainNavProps {
  groups: NavGroup[]
  contextualNav?: ContextualNav | null
  isCollapsed: boolean
  onNavigate?: (href: string) => void
  onCloseContextual?: () => void
}

function NavItemRow({
  item,
  isCollapsed,
  onNavigate,
  indent = false,
}: {
  item: NavItem
  isCollapsed: boolean
  onNavigate?: (href: string) => void
  indent?: boolean
}) {
  const Icon = item.icon || Circle
  const isReadOnly = item.accessMode === 'read'

  return (
    <div className="relative group/navitem">
      <button
        onClick={() => onNavigate?.(item.href)}
        className={`
          w-full flex items-center gap-3 rounded-xl transition-all duration-150 relative motion-reduce:transition-none
          ${isCollapsed ? 'justify-center px-2 py-2.5' : indent ? 'pl-8 pr-3 py-1.5' : 'px-3 py-2'}
          ${item.isActive
            ? 'bg-white/[0.1] text-white'
            : isReadOnly
              ? 'text-slate-500 hover:text-slate-400 hover:bg-white/[0.04]'
              : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.06]'
          }
        `}
      >
        {/* Active indicator bar */}
        {item.isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-indigo-400" />
        )}

        <div className="relative shrink-0">
          <Icon
            className={`${indent ? 'w-4 h-4' : 'w-[18px] h-[18px]'} ${
              item.isActive
                ? 'text-indigo-400'
                : isReadOnly
                  ? 'text-slate-500'
                  : ''
            }`}
            strokeWidth={item.isActive ? 2 : 1.5}
          />
        </div>

        {!isCollapsed && (
          <>
            <span
              className={`flex-1 text-left ${indent ? 'text-[12px]' : 'text-[13px]'} font-medium truncate ${
                isReadOnly ? 'opacity-60' : ''
              }`}
            >
              {item.label}
            </span>

            {isReadOnly && (
              <Eye className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            )}

            {item.badge !== undefined && !isReadOnly && (
              <span
                aria-live="polite"
                className={`ml-auto text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5 ${
                  item.isActive
                    ? 'bg-red-500/30 text-red-300'
                    : 'bg-red-500/20 text-red-400'
                }`}
              >
                {item.badge}
              </span>
            )}
          </>
        )}

        {/* Collapsed badge dot */}
        {isCollapsed && item.badge !== undefined && !isReadOnly && (
          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500" />
        )}
      </button>

      {/* Tooltip on hover when collapsed */}
      {isCollapsed && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2.5 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-medium whitespace-nowrap opacity-0 pointer-events-none group-hover/navitem:opacity-100 transition-opacity duration-150 z-50 shadow-lg">
          {item.label}
          {isReadOnly && <span className="text-slate-400 ml-1">(Read only)</span>}
        </div>
      )}
    </div>
  )
}

function ContextualNavSection({
  contextualNav,
  isCollapsed,
  onNavigate,
  onClose,
}: {
  contextualNav: ContextualNav
  isCollapsed: boolean
  onNavigate?: (href: string) => void
  onClose?: () => void
}) {
  const EntityIcon = contextualNav.entityIcon

  return (
    <div
      role="region"
      aria-label={`${contextualNav.entityName} navigation`}
      className="mx-2 my-2 rounded-xl bg-white/[0.04] border border-white/[0.06] overflow-hidden"
    >
      {/* Entity header */}
      <div className={`flex items-center gap-2 px-3 py-2.5 border-b border-white/[0.06] ${isCollapsed ? 'justify-center' : ''}`}>
        <EntityIcon className="w-4 h-4 text-indigo-400 shrink-0" strokeWidth={2} />
        {!isCollapsed && (
          <>
            <span className="flex-1 text-[12px] font-semibold text-white truncate" title={contextualNav.entityName}>
              {contextualNav.entityName}
            </span>
            <button
              onClick={onClose}
              className="p-0.5 text-slate-500 hover:text-white hover:bg-white/10 rounded transition-colors"
              aria-label={`Close ${contextualNav.entityName} navigation`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>

      {/* Sub-items */}
      <ul className="py-1 space-y-0.5">
        {contextualNav.items.map((item) => (
          <NavItemRow
            key={item.href}
            item={item}
            isCollapsed={isCollapsed}
            onNavigate={onNavigate}
            indent={!isCollapsed}
          />
        ))}
      </ul>

      {/* Collapsed tooltip with entity name */}
      {isCollapsed && (
        <div className="absolute left-full top-0 ml-2 px-2.5 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-medium whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50 shadow-lg">
          {contextualNav.entityName}
        </div>
      )}
    </div>
  )
}

export function MainNav({ groups, contextualNav, isCollapsed, onNavigate, onCloseContextual }: MainNavProps) {
  // Determine which parent nav item the contextual nav should appear under
  const contextualParentHref = contextualNav
    ? contextualNav.entityType === 'association' ? '/associations' : '/circles'
    : null

  return (
    <nav className="space-y-1 px-3" aria-label="Main navigation">
      {groups.map((group, groupIdx) => {
        const visibleItems = group.items.filter(
          (item) => item.accessMode !== 'none'
        )

        if (visibleItems.length === 0) return null

        // Groups starting with _ are unlabeled — show a divider between them
        const isUnlabeled = group.label.startsWith('_')

        return (
          <div key={group.label}>
            {/* Divider between groups (skip first) */}
            {groupIdx > 0 && (
              <div className={`${isCollapsed ? 'mx-auto w-5' : 'mx-3'} my-2 border-t border-white/[0.06]`} />
            )}

            {/* Show group label only for labeled groups */}
            {!isUnlabeled && !isCollapsed && (
              <div className="px-3 py-2 mt-2 first:mt-0">
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                  {group.label}
                </span>
              </div>
            )}

            {/* Group items — contextual nav inserted directly under parent item */}
            <div className="space-y-0.5">
              {visibleItems.map((item) => (
                <div key={item.href}>
                  <NavItemRow
                    item={item}
                    isCollapsed={isCollapsed}
                    onNavigate={onNavigate}
                  />
                  {contextualNav && item.href === contextualParentHref && (
                    <ContextualNavSection
                      contextualNav={contextualNav}
                      isCollapsed={isCollapsed}
                      onNavigate={onNavigate}
                      onClose={onCloseContextual}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </nav>
  )
}
